import { type FormEvent, useRef, useState } from 'react';

import { Button } from '@/modules/ui/components/button/Button';
import { Input } from '@/modules/ui/components/input/Input';

type EmailCollectionState = 'form' | 'shared' | 'dismissed';

interface PrescreenEmailCollectionProps {
  contactName: string;
  onEmailSubmit?: (email: string) => void | Promise<void>;
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * Keeps the email-collection UI separate from the post-prescreen flow so a
 * future API integration can be added at this boundary without changing it.
 */
export function PrescreenEmailCollection({
  contactName,
  onEmailSubmit,
}: PrescreenEmailCollectionProps) {
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [collectionState, setCollectionState] =
    useState<EmailCollectionState>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedEmail = email.trim();

    if (!isValidEmail(trimmedEmail)) {
      setErrorMessage('Enter a valid email address.');
      emailInputRef.current?.focus();
      return;
    }

    setErrorMessage('');
    setIsSubmitting(true);

    try {
      await onEmailSubmit?.(trimmedEmail);
      setCollectionState('shared');
    } catch {
      setErrorMessage('We couldn’t share your email. Please try again.');
      emailInputRef.current?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (collectionState === 'shared') {
    return (
      <p className="text-muted-foreground text-sm" role="status">
        Thanks — {contactName} can reach you at {email.trim()}.
      </p>
    );
  }

  if (collectionState === 'dismissed') {
    return (
      <p className="text-muted-foreground text-sm" role="status">
        No problem — your voice intro is still complete.
      </p>
    );
  }

  return (
    <form className="text-left" noValidate onSubmit={handleSubmit}>
      <p className="text-foreground text-base font-medium">
        Where should {contactName} send your update?
      </p>
      <p className="text-muted-foreground mt-1 text-sm">
        Optional — add your email if you&apos;d prefer to hear back there.
      </p>

      <div className="mt-4">
        <label htmlFor="prescreen-update-email" className="text-sm font-medium">
          Email address
        </label>
        <Input
          ref={emailInputRef}
          id="prescreen-update-email"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          disabled={isSubmitting}
          onChange={(event) => {
            setEmail(event.target.value);
            if (errorMessage) setErrorMessage('');
          }}
          placeholder="you@example.com"
          aria-invalid={Boolean(errorMessage)}
          aria-describedby={errorMessage ? 'prescreen-email-error' : undefined}
          className="mt-2"
        />
        {errorMessage && (
          <p
            id="prescreen-email-error"
            className="text-destructive mt-2 text-sm"
            role="alert"
          >
            {errorMessage}
          </p>
        )}
      </div>

      <div className="mt-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <Button type="submit" size="lg" loading={isSubmitting}>
          Share my email
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="medium"
          onClick={() => setCollectionState('dismissed')}
          disabled={isSubmitting}
        >
          Not now
        </Button>
      </div>
    </form>
  );
}
